import { useState, useEffect } from 'react';
import { usePageTitle } from '@hooks/usePageTitle';
import ContourBG from '@components/ContourBG';

/* ─── Founder portrait with graceful fallback until the photo shoot ─── */
const FounderPortrait = ({ ring }) => {
  const [imgOk, setImgOk] = useState(true);
  return imgOk ? (
    <img src="/founder.jpg" alt="Castle, founder of Castle Atlas"
      onError={() => setImgOk(false)}
      style={{ width: 'clamp(120px,15vw,170px)', height: 'clamp(120px,15vw,170px)', borderRadius: '50%', objectFit: 'cover', border: `3px solid ${ring}`, display: 'block', margin: '0 auto', flexShrink: 0 }}/>
  ) : (
    <div style={{ width: 'clamp(120px,15vw,170px)', height: 'clamp(120px,15vw,170px)', borderRadius: '50%', border: `2px solid ${ring}`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto', flexShrink: 0 }}>
      <img src="/logo.svg" alt="" width={56} height={56}/>
    </div>
  );
};

/* This is Filip as founder — a personal quote, not the cartographer's
   voice. Moved here from Landing's hub so the two narrators never share
   a screen: the cartographer surveys and annotates the chart, Filip
   explains why he built it. Dropped the "THE CARTOGRAPHER" eyebrow that
   used to sit on this exact quote on the old page — that label belonged
   to the chart, not to Filip. */
const About = () => {
  usePageTitle('About — Castle Atlas');

  const [isDark, setIsDark] = useState(() =>
    typeof window !== 'undefined' && document.documentElement.classList.contains('dark')
  );
  useEffect(() => {
    const obs = new MutationObserver(() =>
      setIsDark(document.documentElement.classList.contains('dark'))
    );
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);

  const c = {
    pageBg: isDark ? '#0e0d0c' : '#faf9f7',
    border: isDark ? '#2d2825' : '#e8e4df',
    surface: isDark ? '#1c1917' : '#ffffff',
    head: isDark ? '#fafaf9' : '#1c1917',
    muted: isDark ? '#a8a29e' : '#78716c',
    ring: 'rgba(234,88,12,0.35)',
  };

  return (
    <div id="main-content" tabIndex={-1} style={{ minHeight: '100vh', backgroundColor: c.pageBg }}>
      <div style={{ position: 'relative', overflow: 'hidden', borderBottom: `1px solid ${c.border}` }}>
        <ContourBG isDark={isDark}/>
        <div style={{ maxWidth: 680, margin: '0 auto', padding: '72px 24px 48px', position: 'relative', zIndex: 2 }}>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, color: '#ea580c', letterSpacing: '0.14em' }}>FOUNDER</span>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(2rem,5vw,2.8rem)', fontWeight: 900, letterSpacing: '-0.03em', color: c.head, margin: '14px 0 0', lineHeight: 1.08 }}>
            Why Castle Atlas exists
          </h1>
        </div>
      </div>
      <div style={{ maxWidth: 680, margin: '0 auto', padding: '48px 24px 96px' }}>
        <div style={{ background: c.surface, border: `1px solid ${c.border}`, borderRadius: 24, padding: 'clamp(32px,5vw,56px)' }}>
          <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-8 md:gap-12 items-center">
            <FounderPortrait ring={c.ring}/>
            <div>
              <p style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(1.15rem,2.5vw,1.5rem)', lineHeight: 1.5, color: c.head, fontWeight: 600, letterSpacing: '-0.01em', margin: '0 0 18px' }}>
                "I finished a lot of courses and still couldn't do the thing. Watching isn't learning. So I built the thing that makes you produce, then tells you exactly how it went."
              </p>
              <p style={{ fontSize: 14, fontWeight: 700, color: c.head, margin: 0 }}>
                Castle, founder
                <a href="https://x.com/castleatlas" target="_blank" rel="noopener noreferrer"
                  style={{ marginLeft: 12, color: '#ea580c', textDecoration: 'none' }}>Follow the build →</a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;
